import Link from 'next/link';
import { useState } from 'react';
import Router, { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import AuthLayout from 'src/components/auth/AuthLayout';
import PrimaryButton from 'src/components/common/buttons/PrimaryButton';
import { EmailInput } from 'src/components/common/form/input';
import { MainTitle } from 'src/components/common/titles/MainH1';
import { LOGIN_PAGE_URL } from 'src/constants/links';
import { forgotPasswordSendRetrieveEmail } from 'src/libs/cognito';
import { referralValidationRules } from 'src/libs/validation';
import useServerErrorHandler from 'src/hooks/useServerErrorHandler';
import ModalDialog from 'src/components/common/modals/ModalDialog';
import EmailDialogIcon from 'assets/images/emailDialogIcon.svg';

const ForgotPassword = () => {
    const router = useRouter();
    const [handleServerError] = useServerErrorHandler();
    const [modal, setShowModal] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm({
        mode: 'all',
    });

    const onSubmit = (email: string) => {
        forgotPasswordSendRetrieveEmail(email)
            .then(() => {
                Router.push(`/auth/recovery-password?email=${email}`);
                setShowModal(true);
            })
            .catch((err) => {
                if (
                    !err.message.includes('Cannot reset password') &&
                    !err.message.includes('not found')
                ) {
                    handleServerError(err);
                } else {
                    setShowModal(true);
                }
            });
    };

    return (
        <>
            <ModalDialog
                open={modal}
                onClose={() => setShowModal(false)}
                icon={<EmailDialogIcon />}
                title="Check your email"
                description="Dear Customer, please check your email. We sent a restoration link to your email. Use that link to generate a new password for your account."
                primaryButton={{
                    label: 'Ok, go back to Login',
                    handler: () => router.push(LOGIN_PAGE_URL),
                    className: 'w-[300px]',
                }}
            />
            <AuthLayout>
                <div className="w-full sm:w-auto">
                    <div>
                        <MainTitle>Forgot your password?</MainTitle>
                        <p className="font-mont mt-2 text-sm text-black">
                            Fill out your email address, and we’ll send you
                            instructions to reset your password.
                        </p>
                    </div>
                    <div className="mt-7.5">
                        <form
                            onSubmit={handleSubmit((data) =>
                                onSubmit(data.email),
                            )}
                        >
                            <div className="row flex w-full lg:flex-row flex-col md:w-auto">
                                <div className="pr-0 w-full lg:w-2/3 lg:pr-5 mb-7.5">
                                    <div className="mt-2">
                                        <EmailInput
                                            className="kyc-text-field"
                                            placeholder="Enter"
                                            registerReactHookInput={register(
                                                'email',
                                                referralValidationRules.email,
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="flex w-full lg:w-1/3 md:items-end pr-0 mb-7.5 md:min-w-[11rem]">
                                    <PrimaryButton
                                        label="Restore password"
                                        disabled={!isValid}
                                        paddingX="px-8"
                                        paddingY="py-3"
                                        margin="mb-0 mr-0"
                                        width="w-full"
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="mb-7.5">
                        <Link href={LOGIN_PAGE_URL}>
                            <a className="text-sm text-blue font-bold">
                                Back to Login & Sign Up
                            </a>
                        </Link>
                    </div>
                </div>
            </AuthLayout>
        </>
    );
};

export default ForgotPassword;
